import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '@cortex/web-design-system/components/ui/Card';
import { ChildTicketsModal } from './ChildTicketsModal.js';
import { ProjectTable } from './ProjectTable.js';
import { HealthInfoModal } from '../health/index.js';
import type { Project } from '../../../types/project.js';

/** 保留中プロジェクト一覧ビューの props。 */
export interface OnHoldProjectsViewProps {
  /** 表示する保留中プロジェクト一覧。 */
  projects: Project[];
  /** プロジェクト一覧（進行中）へ戻るハンドラ。 */
  onBack: () => void;
}

/**
 * @graph-node {Page}
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 保留中プロジェクト一覧ビュー。ProjectsView の「保留PJ」サブ画面として、保留中案件のテーブルと子チケットドリルダウンモーダルを表示する
 * @graph-connects ProjectTable [calls] 保留中プロジェクトテーブル描画
 * @graph-connects ChildTicketsModal [calls] 子チケットモーダル描画
 * @graph-connects HealthInfoModal [calls] 健康度説明モーダル描画
 */
export function OnHoldProjectsView({ projects, onBack }: OnHoldProjectsViewProps) {
  const { t } = useTranslation();
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [showHealthInfo, setShowHealthInfo] = useState(false);

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="animate-content-reveal">
        <div className="flex items-center gap-3">
          <button
            type="button"
            className="text-[var(--color-accent-primary)] hover:underline text-sm"
            onClick={onBack}
          >
            ← {t('projects.title')}
          </button>
        </div>
        <h1 className="text-2xl font-bold text-[var(--color-text-primary)] mt-2">
          {t('projects.onHoldProjects')}
        </h1>
        <p className="mt-1 text-[var(--color-text-secondary)]">{t('projects.description')}</p>
      </div>

      <Card className="animate-content-reveal">
        <CardContent>
          <ProjectTable
            integratedProducts={[]}
            standaloneProjects={projects}
            onProjectClick={setSelectedProject}
            onHealthInfoClick={() => {
              setShowHealthInfo(true);
            }}
            emptyMessage={t('projects.noOnHold')}
          />
        </CardContent>
      </Card>

      <ChildTicketsModal
        open={selectedProject !== null}
        onClose={() => {
          setSelectedProject(null);
        }}
        project={selectedProject}
      />
      <HealthInfoModal
        open={showHealthInfo}
        onClose={() => {
          setShowHealthInfo(false);
        }}
      />
    </div>
  );
}
